import {format} from 'date-fns'
import { TRIAL_SUBSCRIPTION_DAYS } from './subs'

export const formatDate = (date: string, formatString: string = 'MMM dd yyyy') => {
  return format(new Date(date), formatString)
}

/**
 * Formats a date to a localized string with month, day, and year
 * @param date - Date object, string, or timestamp
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string
 */
export const formatLocalizedDate = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }
): string => {
  const dateObj = typeof date === 'number' ? new Date(date) : new Date(date)
  return dateObj.toLocaleDateString('en-US', options)
}

/**
 * Formats a date for billing/subscription purposes
 * @param date - Date object, string, or timestamp
 * @returns Formatted billing date string (e.g., "Jan 15, 2024")
 */
export const formatBillingDate = (date: Date | string | number): string => {
  return formatLocalizedDate(date, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })
}

/**
 * Creates a future billing date (14 days from now)
 * @returns Formatted future billing date string
 */
export const getFutureBillingDate = (daysFromNow: number = TRIAL_SUBSCRIPTION_DAYS): string => {
  const futureDate = new Date()
  futureDate.setDate(futureDate.getDate() + daysFromNow)
  return formatBillingDate(futureDate)
}

/**
 * Formats a timestamp from milliseconds to a localized date string
 * @param timestamp - Timestamp in milliseconds
 * @returns Formatted date string
 */
export const formatTimestampToDate = (timestamp: number): string => {
  return formatLocalizedDate(timestamp)
}

/**
 * Returns the UTC offset label for a given IANA timezone string.
 * @param timeZone - IANA timezone string (e.g., 'Australia/Melbourne')
 * @returns Offset label string for display
 */
export function getTimezoneOffsetLabel(timeZone: string): string {
  if (!timeZone) return timeZone;
  const now = new Date();
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
    });
    const parts = dtf.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    const offset = offsetPart ? offsetPart.value : '';

    let match = offset.match(/(?:UTC|GMT)\s*([+-])(\d{1,2})(?::(\d{2}))?/);
    if (match) {
      const sign = match[1];
      const hour = match[2].padStart(2, '0');
      const minute = match[3] ? match[3] : '00';
      return `(UTC${sign}${hour}:${minute})`;
    }

    if (offset === 'UTC' || offset === 'GMT') {
      return '(UTC+00:00)';
    }
   
    return offset ? `(${offset})` : '';
  } catch {
    return '';
  }
}